import React, { useRef, useState, useCallback, useEffect } from "react";

interface ZoomFocusPickerProps {
  xPct: number;
  yPct: number;
  scale: number;
  videoSrc: string;
  onPositionChange: (xPct: number, yPct: number) => void;
  onScaleChange?: (scale: number) => void;
}

type ResizeHandleType = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null;
type DragMode = 'move' | 'resize' | null;

/**
 * Visual focus position picker with a draggable crosshair and resizable focus box.
 * Shows video thumbnail with a draggable/resizable focus indicator that maintains aspect ratio.
 */
export const ZoomFocusPicker: React.FC<ZoomFocusPickerProps> = ({
  xPct,
  yPct,
  scale,
  videoSrc,
  onPositionChange,
  onScaleChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandleType>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, xPct: 0, yPct: 0, width: 0, height: 0 });

  // Update container size
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Calculate parent aspect ratio
  const parentAspectRatio = containerSize.width > 0 && containerSize.height > 0
    ? containerSize.width / containerSize.height
    : 16 / 9; // Default to 16:9

  // Calculate focus box size based on scale (maintaining aspect ratio)
  const focusBoxSize = {
    width: Math.max(5, Math.min(95, (100 / scale) * 0.8)),
    height: Math.max(5, Math.min(95, (100 / scale) * 0.8 / parentAspectRatio)),
  };

  // Ensure height maintains aspect ratio
  const adjustedFocusBoxSize = {
    width: focusBoxSize.width,
    height: focusBoxSize.width / parentAspectRatio,
  };

  // Calculate position from mouse event
  const getPositionFromEvent = useCallback(
    (e: MouseEvent | React.MouseEvent): { xPct: number; yPct: number } => {
      if (!containerRef.current) return { xPct: 50, yPct: 50 };

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const xPct = Math.max(0, Math.min(100, (x / rect.width) * 100));
      const yPct = Math.max(0, Math.min(100, (y / rect.height) * 100));

      return { xPct, yPct };
    },
    []
  );

  // Handle resize from corner
  const handleResizeStart = useCallback((e: React.MouseEvent, handle: ResizeHandleType) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeHandle(handle);
    setDragMode('resize');
    setIsDragging(true);
    
    const pos = getPositionFromEvent(e);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      xPct: pos.xPct,
      yPct: pos.yPct,
      width: adjustedFocusBoxSize.width,
      height: adjustedFocusBoxSize.height,
    });
  }, [getPositionFromEvent, adjustedFocusBoxSize]);

  // Handle box drag start
  const handleBoxMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start drag if clicking on resize handles or center handle
    if ((e.target as HTMLElement).closest('.resize-handle, .center-handle')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    setDragMode('move');
    setIsDragging(true);
    
    const pos = getPositionFromEvent(e);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      xPct: pos.xPct,
      yPct: pos.yPct,
      width: adjustedFocusBoxSize.width,
      height: adjustedFocusBoxSize.height,
    });
  }, [getPositionFromEvent, adjustedFocusBoxSize]);

  // Handle center handle drag start
  const handleCenterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragMode('move');
    setIsDragging(true);
    
    const pos = getPositionFromEvent(e);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      xPct: pos.xPct,
      yPct: pos.yPct,
      width: adjustedFocusBoxSize.width,
      height: adjustedFocusBoxSize.height,
    });
  }, [getPositionFromEvent, adjustedFocusBoxSize]);

  // Handle click on container (outside box)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't handle click if clicking on box or handles
      if ((e.target as HTMLElement).closest('.focus-box, .resize-handle, .center-handle')) {
        return;
      }
      const pos = getPositionFromEvent(e);
      onPositionChange(pos.xPct, pos.yPct);
    },
    [getPositionFromEvent, onPositionChange]
  );

  // Handle drag and resize
  useEffect(() => {
    if (!isDragging || !dragMode) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      const deltaXPct = (deltaX / rect.width) * 100;
      const deltaYPct = (deltaY / rect.height) * 100;

      if (dragMode === 'move') {
        // Move the box
        const newXPct = Math.max(
          adjustedFocusBoxSize.width / 2,
          Math.min(100 - adjustedFocusBoxSize.width / 2, dragStart.xPct + deltaXPct)
        );
        const newYPct = Math.max(
          adjustedFocusBoxSize.height / 2,
          Math.min(100 - adjustedFocusBoxSize.height / 2, dragStart.yPct + deltaYPct)
        );
        onPositionChange(newXPct, newYPct);
      } else if (dragMode === 'resize' && resizeHandle) {
        // Resize maintaining aspect ratio
        // Get current mouse position as percentage
        const mouseXPct = Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100));
        const mouseYPct = Math.max(0, Math.min(100, (e.clientY - rect.top) / rect.height * 100));
        
        // Calculate the opposite corner position (fixed point)
        const centerX = dragStart.xPct;
        const centerY = dragStart.yPct;
        const halfWidth = dragStart.width / 2;
        const halfHeight = dragStart.height / 2;
        
        let oppositeCornerX: number, oppositeCornerY: number;
        if (resizeHandle === 'top-left') {
          oppositeCornerX = centerX + halfWidth; // bottom-right corner
          oppositeCornerY = centerY + halfHeight;
        } else if (resizeHandle === 'top-right') {
          oppositeCornerX = centerX - halfWidth; // bottom-left corner
          oppositeCornerY = centerY + halfHeight;
        } else if (resizeHandle === 'bottom-left') {
          oppositeCornerX = centerX + halfWidth; // top-right corner
          oppositeCornerY = centerY - halfHeight;
        } else { // bottom-right
          oppositeCornerX = centerX - halfWidth; // top-left corner
          oppositeCornerY = centerY - halfHeight;
        }
        
        // Calculate distance from opposite corner to mouse position
        const deltaX = mouseXPct - oppositeCornerX;
        const deltaY = mouseYPct - oppositeCornerY;
        
        // Calculate new size maintaining aspect ratio
        // Use the dimension that gives the smaller size to maintain aspect ratio
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);
        const newHalfWidth = Math.max(2.5, Math.min(47.5, Math.min(absDeltaX, absDeltaY * parentAspectRatio)));
        const newHalfHeight = newHalfWidth / parentAspectRatio;
        
        // Calculate new center position (midpoint between mouse and opposite corner)
        let newCenterXPct: number, newCenterYPct: number;
        if (resizeHandle === 'top-left') {
          newCenterXPct = (mouseXPct + oppositeCornerX) / 2;
          newCenterYPct = (mouseYPct + oppositeCornerY) / 2;
        } else if (resizeHandle === 'top-right') {
          newCenterXPct = (mouseXPct + oppositeCornerX) / 2;
          newCenterYPct = (mouseYPct + oppositeCornerY) / 2;
        } else if (resizeHandle === 'bottom-left') {
          newCenterXPct = (mouseXPct + oppositeCornerX) / 2;
          newCenterYPct = (mouseYPct + oppositeCornerY) / 2;
        } else { // bottom-right
          newCenterXPct = (mouseXPct + oppositeCornerX) / 2;
          newCenterYPct = (mouseYPct + oppositeCornerY) / 2;
        }
        
        // Clamp center position to keep box within bounds
        newCenterXPct = Math.max(newHalfWidth, Math.min(100 - newHalfWidth, newCenterXPct));
        newCenterYPct = Math.max(newHalfHeight, Math.min(100 - newHalfHeight, newCenterYPct));
        
        // Calculate new scale from size
        const newWidth = newHalfWidth * 2;
        const newScale = 100 / (newWidth / 0.8);
        
        // Update position and scale
        onPositionChange(newCenterXPct, newCenterYPct);
        if (onScaleChange) {
          onScaleChange(newScale);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragMode(null);
      setResizeHandle(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragMode, resizeHandle, dragStart, adjustedFocusBoxSize, parentAspectRatio, onPositionChange, onScaleChange]);

  const handleSize = 10;
  const handleOffset = -handleSize / 2;

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      className="relative w-full aspect-video bg-muted rounded-md overflow-hidden cursor-crosshair border"
    >
      {/* Video thumbnail background */}
      <video
        src={videoSrc}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-60"
        muted
        playsInline
      />

      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Vertical lines */}
        <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/20" />
        <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/20" />
        {/* Horizontal lines */}
        <div className="absolute top-1/3 left-0 right-0 h-px bg-white/20" />
        <div className="absolute top-2/3 left-0 right-0 h-px bg-white/20" />
      </div>

      {/* Focus area indicator with enhanced visual markers */}
      <div
        className={`focus-box absolute border-[3px] border-primary bg-primary/10 rounded transition-all cursor-grab ${
          isDragging && dragMode === 'move' ? "opacity-80 cursor-grabbing" : "opacity-100"
        }`}
        style={{
          left: `${xPct}%`,
          top: `${yPct}%`,
          width: `${adjustedFocusBoxSize.width}%`,
          height: `${adjustedFocusBoxSize.height}%`,
          transform: "translate(-50%, -50%)",
          boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.3), 0 0 8px rgba(59, 130, 246, 0.2)",
        }}
        onMouseDown={handleBoxMouseDown}
      >
        {/* Corner indicators */}
        <div className="absolute -top-1 -left-1 w-2 h-2 bg-primary border-2 border-white rounded-sm" />
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary border-2 border-white rounded-sm" />
        <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-primary border-2 border-white rounded-sm" />
        <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-primary border-2 border-white rounded-sm" />
        
        {/* Edge indicators */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-2 bg-primary" />
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-1 h-2 bg-primary" />
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-2 h-1 bg-primary" />
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-2 h-1 bg-primary" />

        {/* Crosshair */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-full h-px bg-primary/50" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-full w-px bg-primary/50" />
        </div>
        {/* Center dot */}
        <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-primary rounded-full transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        
        {/* Scale indicator */}
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-[10px] text-primary font-medium bg-black/70 px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none">
          {scale.toFixed(1)}x zoom
        </div>

        {/* Resize handles at corners */}
        <div
          className="resize-handle absolute bg-white border-2 border-primary rounded-sm cursor-nwse-resize hover:scale-110 transition-transform z-10"
          style={{
            width: `${handleSize}px`,
            height: `${handleSize}px`,
            left: `${handleOffset}px`,
            top: `${handleOffset}px`,
          }}
          onMouseDown={(e) => handleResizeStart(e, 'top-left')}
        />
        <div
          className="resize-handle absolute bg-white border-2 border-primary rounded-sm cursor-nesw-resize hover:scale-110 transition-transform z-10"
          style={{
            width: `${handleSize}px`,
            height: `${handleSize}px`,
            right: `${handleOffset}px`,
            top: `${handleOffset}px`,
          }}
          onMouseDown={(e) => handleResizeStart(e, 'top-right')}
        />
        <div
          className="resize-handle absolute bg-white border-2 border-primary rounded-sm cursor-nesw-resize hover:scale-110 transition-transform z-10"
          style={{
            width: `${handleSize}px`,
            height: `${handleSize}px`,
            left: `${handleOffset}px`,
            bottom: `${handleOffset}px`,
          }}
          onMouseDown={(e) => handleResizeStart(e, 'bottom-left')}
        />
        <div
          className="resize-handle absolute bg-white border-2 border-primary rounded-sm cursor-nwse-resize hover:scale-110 transition-transform z-10"
          style={{
            width: `${handleSize}px`,
            height: `${handleSize}px`,
            right: `${handleOffset}px`,
            bottom: `${handleOffset}px`,
          }}
          onMouseDown={(e) => handleResizeStart(e, 'bottom-right')}
        />
      </div>

      {/* Draggable handle at focus center */}
      <div
        className={`center-handle absolute w-6 h-6 rounded-full bg-primary border-2 border-white shadow-lg cursor-grab transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-20 ${
          isDragging && dragMode === 'move' ? "cursor-grabbing scale-110" : "hover:scale-110"
        } transition-transform`}
        style={{
          left: `${xPct}%`,
          top: `${yPct}%`,
        }}
        onMouseDown={handleCenterMouseDown}
      >
        <div className="w-2 h-2 bg-white rounded-full" />
      </div>

      {/* Labels */}
      <div className="absolute bottom-1 left-1 text-[10px] text-white/70 bg-black/50 px-1 rounded pointer-events-none">
        Click or drag to set focus
      </div>
      <div className="absolute top-1 right-1 text-[10px] text-white/70 bg-black/50 px-1 rounded pointer-events-none">
        {Math.round(xPct)}%, {Math.round(yPct)}%
      </div>
    </div>
  );
};
